// Global search bar (PR #137, 2026-05-22). Persistent in the Workflow
// Shell header — every page, every route. Operator-flagged need:
// reps should be able to type a project name / client name / project# /
// PO# / Bill# / Invoice# / customer PO and jump straight to it.
//
// **UX:**
//   - 250 ms debounce keeps key-by-key load off the API.
//   - ⌘K / Ctrl+K from anywhere focuses the input.
//   - Dropdown groups results by entity type. Each row: peach-100 chip
//     with entity label + primary identifier + secondary context.
//   - Esc / blur closes the dropdown. Arrow keys + Enter navigate.
//   - Brand-locked palette throughout.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Briefcase,
  FileText,
  Folder,
  Receipt,
  Search,
  ShoppingCart,
  Truck,
  User,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface ClientHit {
  id: string;
  name: string;
  status: string | null;
}
interface ProjectHit {
  id: string;
  projectNumber: number;
  name: string;
  clientId: string;
  clientName: string;
  statusStage: string;
}
interface OrderHit {
  id: string;
  orderNumber: number;
  projectId: string;
  projectName: string;
  clientName: string;
  customerPo: string | null;
  statusStage: string;
}
interface PresentationHit {
  id: string;
  presentationNumber: number;
  projectId: string;
  projectName: string;
  clientName: string;
  status: string;
}
interface PurchaseOrderHit {
  id: string;
  poNumber: number;
  orderId: string;
  orderNumber: number;
  vendorName: string;
  internalStage: string;
}
interface BillHit {
  id: string;
  vendorBillNumber: string | null;
  vendorOrderReference: string | null;
  vendorName: string;
  status: string;
  total: string;
}
interface InvoiceHit {
  id: string;
  invoiceNumber: number;
  orderId: string;
  orderNumber: number;
  clientName: string;
  total: string;
  status: string;
}
interface SearchResult {
  clients: ClientHit[];
  projects: ProjectHit[];
  orders: OrderHit[];
  presentations: PresentationHit[];
  purchaseOrders: PurchaseOrderHit[];
  bills: BillHit[];
  invoices: InvoiceHit[];
  totalHits: number;
}

interface NavRow {
  /** Stable react key. */
  key: string;
  /** Visual label of the entity type. */
  chip: string;
  Icon: typeof Search;
  primary: string;
  secondary: string;
  href: string;
}

export function GlobalSearchBar() {
  const [input, setInput] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // 250 ms debounce.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 250);
    return () => clearTimeout(t);
  }, [input]);

  // ⌘K / Ctrl+K from anywhere focuses the input.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === "Escape" && open) {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ["global-search", debounced],
    queryFn: () =>
      api<SearchResult>(`/search?q=${encodeURIComponent(debounced)}&limit=5`),
    enabled: debounced.length > 0 && open,
    // Cache briefly so backspacing into the same query is instant.
    staleTime: 30_000,
  });

  const navRows = useMemo<NavRow[]>(() => {
    if (!data) return [];
    const rows: NavRow[] = [];
    for (const c of data.clients) {
      rows.push({
        key: `client-${c.id}`,
        chip: "Client",
        Icon: User,
        primary: c.name,
        secondary: c.status ? `Status: ${c.status}` : "",
        href: `/clients/${c.id}`,
      });
    }
    for (const p of data.projects) {
      rows.push({
        key: `project-${p.id}`,
        chip: "Project",
        Icon: Folder,
        primary: `#${p.projectNumber} · ${p.name}`,
        secondary: `${p.clientName} · ${p.statusStage}`,
        href: `/projects/${p.id}`,
      });
    }
    for (const pr of data.presentations) {
      rows.push({
        key: `presentation-${pr.id}`,
        chip: "Presentation",
        Icon: FileText,
        primary: `P-${pr.presentationNumber}`,
        secondary: `${pr.clientName} · ${pr.projectName} · ${pr.status}`,
        href: `/presentations/${pr.id}`,
      });
    }
    for (const o of data.orders) {
      rows.push({
        key: `order-${o.id}`,
        chip: "Order",
        Icon: ShoppingCart,
        primary: `#${o.orderNumber}${
          o.customerPo ? ` · Customer PO ${o.customerPo}` : ""
        }`,
        secondary: `${o.clientName} · ${o.projectName} · ${o.statusStage}`,
        href: `/orders/${o.id}`,
      });
    }
    for (const po of data.purchaseOrders) {
      rows.push({
        key: `purchase-order-${po.id}`,
        chip: "PO",
        Icon: Truck,
        primary: `PO #${po.poNumber}`,
        secondary: `${po.vendorName} · Order #${po.orderNumber} · ${po.internalStage}`,
        href: `/orders/${po.orderId}`,
      });
    }
    for (const b of data.bills) {
      rows.push({
        key: `bill-${b.id}`,
        chip: "Bill",
        Icon: Briefcase,
        primary: `${b.vendorBillNumber ?? b.vendorOrderReference ?? "(no number)"}`,
        secondary: `${b.vendorName} · $${b.total} · ${b.status}`,
        href: `/bills/${b.id}`,
      });
    }
    for (const inv of data.invoices) {
      rows.push({
        key: `invoice-${inv.id}`,
        chip: "Invoice",
        Icon: Receipt,
        primary: `Invoice #${inv.invoiceNumber}`,
        secondary: `${inv.clientName} · Order #${inv.orderNumber} · $${inv.total} · ${inv.status}`,
        href: `/invoices/${inv.id}`,
      });
    }
    return rows;
  }, [data]);

  // Reset active row when the result list changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [navRows]);

  const select = useCallback(
    (row: NavRow) => {
      setOpen(false);
      setInput("");
      setDebounced("");
      navigate(row.href);
    },
    [navigate],
  );

  const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, navRows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && navRows.length > 0) {
      e.preventDefault();
      const row = navRows[activeIndex];
      if (row) select(row);
    }
  };

  const showDropdown = open && debounced.length > 0;

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-xl"
      role="combobox"
      aria-expanded={showDropdown}
      aria-haspopup="listbox"
      aria-controls="global-search-listbox"
    >
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brown-300" />
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={onInputKeyDown}
          placeholder="Search projects, clients, PO#, Bill#, Invoice#…"
          aria-label="Global search"
          className="h-9 w-full rounded-md border border-brown-50 bg-cream-50/60 pl-9 pr-16 text-sm text-brown-500 placeholder:text-[#8a8a85] focus:border-peach-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-peach-200"
        />
        {input.length > 0 ? (
          <button
            type="button"
            onClick={() => {
              setInput("");
              setDebounced("");
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-brown-300 hover:bg-cream-100 hover:text-brown-500"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <kbd className="absolute right-2 top-1/2 -translate-y-1/2 rounded border border-brown-100 bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold text-brown-300">
            ⌘K
          </kbd>
        )}
      </div>

      {showDropdown && (
        <div
          id="global-search-listbox"
          role="listbox"
          className="absolute left-0 right-0 top-11 z-30 max-h-[70vh] overflow-y-auto rounded-md border border-brown-100 bg-white shadow-xl"
        >
          {isFetching && navRows.length === 0 ? (
            <p className="px-4 py-3 text-sm text-brown-300">Searching…</p>
          ) : navRows.length === 0 ? (
            <p className="px-4 py-3 text-sm text-brown-300">
              No matches for &ldquo;{debounced}&rdquo;.
            </p>
          ) : (
            <ul>
              {navRows.map((row, i) => {
                const RowIcon = row.Icon;
                const active = i === activeIndex;
                return (
                  <li key={row.key} role="option" aria-selected={active}>
                    <button
                      type="button"
                      onMouseEnter={() => setActiveIndex(i)}
                      onClick={() => select(row)}
                      className={cn(
                        "flex w-full items-center gap-3 border-b border-brown-50 px-4 py-2.5 text-left text-sm last:border-b-0",
                        active ? "bg-peach-50" : "hover:bg-cream-50",
                      )}
                    >
                      <RowIcon className="h-4 w-4 flex-shrink-0 text-brown-300" />
                      <span className="inline-block rounded-full bg-peach-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brown-500">
                        {row.chip}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-semibold text-brown-500">
                          {row.primary}
                        </span>
                        {row.secondary && (
                          <span className="block truncate text-xs text-brown-300">
                            {row.secondary}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {data && data.totalHits > navRows.length && (
            <p className="border-t border-brown-50 px-4 py-2 text-[11px] text-brown-300">
              Showing top results. Refine your query for more.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
